// Shared pieces of the report document specs: the column builder that takes
// its header and kind from the screen's column catalogue (the planner sizes
// the columns from their content), the product noun and the percent format
// the screen tables use.
//
// Pure on purpose: no react-pdf, nothing from ../../components or a document
// file. The specs are imported by the report PAGES (main bundle) to count
// pages in the row picker, and by the documents (lazy pdf chunk) to print.
import { translate } from "shared/languages";
import { ColumnKind, REPORT_METRICS, ReportMetric } from "shared/reports/columnCatalogue";
import { PackageType } from "shared/types/analytics";
import { PdfColumn } from "../../pagePlan";

export type ColumnDef<Row> = {
  // The catalogue metric: supplies the header (one name per metric) and the
  // kind (which decides whether the column may wrap). A column with its own
  // header passes `kind`.
  metric?: ReportMetric;
  header?: string;
  kind?: ColumnKind;
  render: (row: Row) => string;
  sub?: (row: Row) => string | null;
};

// PDF columns, all left-aligned, carrying the catalogue kind so the planner
// knows which may wrap. The flex here is a placeholder: createDocPlanner
// replaces it with the measured width (pagePlan.ts fitColumns).
export const columns = <Row>(defs: ColumnDef<Row>[]): PdfColumn<Row>[] =>
  defs.map((d) => ({
    header: d.header ?? (d.metric ? translate(REPORT_METRICS[d.metric].label) : ""),
    flex: 1,
    align: "left" as const,
    render: d.render,
    ...(d.sub ? { sub: d.sub } : {}),
    kind: d.kind ?? (d.metric ? REPORT_METRICS[d.metric].kind : "text"),
  }));

export const productNoun = (type: PackageType | "paket" | "membership"): string =>
  translate(type === "membership" ? "analyticsPage.tab.membership" : "analyticsPage.tab.package");

// The plural, Title Case, for the labels that count several ("Packages
// Sold", "Compare Packages"), the words the screen uses; Indonesian has no
// plural form, so its keys repeat the noun.
export const productNounPlural = (type: PackageType | "paket" | "membership"): string =>
  translate(type === "membership" ? "pdf.nouns.membership" : "pdf.nouns.package");

// A percentage exactly as the screen prints it (`${value}%`, dot decimal).
export const pct = (v: number) => `${v}%`;

